import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  type CarrierTransportServiceRow,
  useCreateCarrierService,
  useUpdateCarrierService,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { hasHttpStatus } from "@/lib/api-error";
import { isServiceCode, normalizeServiceCode } from "@/lib/service-code";

interface EditServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = create a new service. */
  service: CarrierTransportServiceRow | null;
}

/**
 * Create or edit a transport service — the carrier's actual shipping product.
 *
 * A carrier knows what it can carry and how fast; EZX does not. This used to be
 * admin-only data entry, so every new service a carrier wanted meant asking us.
 *
 * `code` is immutable once set: quoting, the seed and every rate band are
 * pinned to it, and renaming it would silently unprice the service. The name is
 * the part humans read, and it is free to change.
 */
export function EditServiceDialog({
  open,
  onOpenChange,
  service,
}: EditServiceDialogProps) {
  const create = useCreateCarrierService();
  const update = useUpdateCarrierService();
  const isEdit = service !== null;

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [estimatedDays, setEstimatedDays] = useState("");
  // Volumetric divisors and package-fit ceilings. Blank means "no limit", and
  // is sent as null — the Server distinguishes that from an omitted field,
  // which means "leave whatever is there alone".
  const [dimFactorCm, setDimFactorCm] = useState("");
  const [dimFactorInch, setDimFactorInch] = useState("");
  const [maxWeightKg, setMaxWeightKg] = useState("");
  const [maxLongestSideCm, setMaxLongestSideCm] = useState("");
  const [maxLengthPlusGirthCm, setMaxLengthPlusGirthCm] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submissionLock = useRef(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);
    submissionLock.current = false;
    setCodeTouched(false);
    setName(service ? String(service.name ?? "") : "");
    setCode(service ? String(service.code ?? "") : "");
    setEstimatedDays(
      service?.estimatedDays == null ? "" : String(service.estimatedDays)
    );
    setIsActive(service ? service.isActive !== false : true);
    const num = (value: unknown) => (value == null ? "" : String(value));
    setDimFactorCm(num(service?.dimFactorCm));
    setDimFactorInch(num(service?.dimFactorInch));
    setMaxWeightKg(num(service?.maxWeightKg));
    setMaxLongestSideCm(num(service?.maxLongestSideCm));
    setMaxLengthPlusGirthCm(num(service?.maxLengthPlusGirthCm));
  }, [open, service]);

  /** Blank clears the value (null); anything else is sent as a number. */
  const limit = (value: string) => {
    const trimmed = value.trim();
    return trimmed === "" ? null : Number(trimmed);
  };

  // The code is derived from the name until the carrier overrides it — one
  // field to fill instead of two, and the derived value already satisfies the
  // server's pattern.
  const effectiveCode = codeTouched ? code : normalizeServiceCode(name);
  const codeValid = isServiceCode(effectiveCode);
  const pending = submitting || create.isPending || update.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submissionLock.current) return;

    setError(null);
    const trimmedName = name.trim();
    const days = estimatedDays.trim();
    if (!trimmedName) {
      setError("Enter a service name.");
      return;
    }
    if (!isEdit && !codeValid) {
      setError(
        effectiveCode
          ? "Use only lowercase letters, digits and underscores for the code."
          : "Enter a service code."
      );
      return;
    }
    if (days !== "" && (!/^\d+$/.test(days) || Number(days) < 0)) {
      setError("Estimated days must be a whole number of zero or more.");
      return;
    }

    submissionLock.current = true;
    setSubmitting(true);
    try {
      if (isEdit) {
        await update.mutateAsync({
          serviceId: String(service.id),
          patch: {
            name: trimmedName,
            estimatedDays: days === "" ? null : Number(days),
            dimFactorCm: limit(dimFactorCm),
            dimFactorInch: limit(dimFactorInch),
            maxWeightKg: limit(maxWeightKg),
            maxLongestSideCm: limit(maxLongestSideCm),
            maxLengthPlusGirthCm: limit(maxLengthPlusGirthCm),
            isActive,
          },
        });
      } else {
        await create.mutateAsync({
          code: effectiveCode,
          name: trimmedName,
          estimatedDays: days === "" ? null : Number(days),
        });
      }
      toast.success(isEdit ? "Service saved." : "Service added.");
      onOpenChange(false);
    } catch (err) {
      const res = (err as { response?: { data?: { error?: string } } })
        ?.response?.data;
      // The server speaks in codes. A carrier should read a sentence — and
      // NO_PLATFORM_FEE in particular is not the carrier's fault to fix.
      if (res?.error === "NO_PLATFORM_FEE") {
        setError(
          "EZX has not set a fee for your account yet, so this service could not be quoted. Contact EZX to finish setup."
        );
      } else if (res?.error === "DUPLICATE_CODE") {
        setError("You already have a service with this code. Edit that one.");
      } else if (hasHttpStatus(err, 403)) {
        setError("You do not have permission to manage shipping services.");
      } else {
        setError(res?.error ?? "Could not save the service. Try again.");
      }
    } finally {
      submissionLock.current = false;
      setSubmitting(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && pending) return;
    onOpenChange(nextOpen);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title={
        isEdit ? `Edit ${String(service.name ?? "service")}` : "Add service"
      }
      presentation="responsive-sheet"
    >
      <form noValidate onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-muted-foreground">
          A service is what a customer picks at checkout. Once it exists, give
          it a rate band so it can be quoted.
        </p>

        <div>
          <label htmlFor="svc-name" className="block text-sm font-medium mb-1">
            Name
          </label>
          <Input
            id="svc-name"
            required
            placeholder="Saturday Delivery"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={pending}
          />
        </div>

        <div>
          <label htmlFor="svc-code" className="block text-sm font-medium mb-1">
            Code
          </label>
          <Input
            id="svc-code"
            required
            className="font-mono"
            value={effectiveCode}
            onChange={(e) => {
              setCodeTouched(true);
              setCode(e.target.value);
            }}
            disabled={pending || isEdit}
          />
          <p className="mt-1 text-sm text-muted-foreground">
            {isEdit
              ? "The code cannot change — your rate bands and past shipments are pinned to it."
              : "Lowercase letters, digits and underscores. Cannot be changed later."}
          </p>
          {!isEdit && effectiveCode !== "" && !codeValid && (
            <p className="mt-1 text-sm text-destructive">
              Use only lowercase letters, digits and underscores.
            </p>
          )}
        </div>

        <div>
          <label htmlFor="svc-days" className="block text-sm font-medium mb-1">
            Estimated days (blank = unspecified)
          </label>
          <Input
            id="svc-days"
            type="number"
            min={0}
            inputMode="numeric"
            className="w-full sm:w-40"
            value={estimatedDays}
            onChange={(e) => setEstimatedDays(e.target.value)}
            disabled={pending}
          />
        </div>

        <fieldset className="space-y-3 rounded-md border p-3">
          <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Volumetric and package limits
          </legend>
          <p className="text-xs text-muted-foreground">
            Blank means no limit. These drive quoting, so clearing a ceiling
            lets bigger parcels be quoted on this service.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <NumberField
              id="svc-dim-cm"
              label="DIM factor (cm³/kg)"
              value={dimFactorCm}
              onChange={setDimFactorCm}
              disabled={pending}
            />
            <NumberField
              id="svc-dim-in"
              label="DIM factor (in³/lb)"
              value={dimFactorInch}
              onChange={setDimFactorInch}
              disabled={pending}
            />
            <NumberField
              id="svc-max-weight"
              label="Max weight (kg)"
              value={maxWeightKg}
              onChange={setMaxWeightKg}
              disabled={pending}
            />
            <NumberField
              id="svc-max-side"
              label="Max longest side (cm)"
              value={maxLongestSideCm}
              onChange={setMaxLongestSideCm}
              disabled={pending}
            />
            <NumberField
              id="svc-max-girth"
              label="Max length + girth (cm)"
              value={maxLengthPlusGirthCm}
              onChange={setMaxLengthPlusGirthCm}
              disabled={pending}
            />
          </div>
        </fieldset>

        {isEdit && (
          <div className="space-y-1">
            <label
              htmlFor="svc-active"
              className="flex min-h-11 items-center gap-3 text-sm"
            >
              <Checkbox
                id="svc-active"
                checked={isActive}
                onCheckedChange={(checked) => setIsActive(checked === true)}
                disabled={pending}
                className="h-5 w-5"
              />
              Available for new quotes
            </label>
            <p className="text-sm text-muted-foreground">
              Turning this off retires the service. It stops being quoted; the
              shipments already using it are untouched.
            </p>
          </div>
        )}

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            size="touch"
            onClick={() => handleOpenChange(false)}
            disabled={pending}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="touch"
            disabled={pending}
            className="w-full sm:w-auto"
          >
            {pending ? "Saving..." : isEdit ? "Save" : "Add service"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** A blank-able positive number field: blank is "no limit", not zero. */
function NumberField({
  id,
  label,
  value,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-medium">
        {label}
      </label>
      <Input
        id={id}
        type="number"
        min={0}
        step="any"
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
    </div>
  );
}
